import { traverseTree } from '@proposify/frontend-utils/traverse';
import type { components } from '@proposify/variables-bff-api-client';
import i18next from 'i18next';
import { z } from 'zod';

// Generated from openapi.yaml — compile-time drift check only (see below), not a
// runtime validator.
type GeneratedVariableNode = components['schemas']['VariableNode'];

export type VariableEntityType = 'client' | 'proposal' | 'user';

export const VariableDomain = {
  DOCUMENT: 'Document',
  CLIENT: 'Client',
  USER: 'User',
  INTEGRATIONS: 'Integrations',
  /** @deprecated Legacy API name used only for the path replace in getCRMVariables */
  CRM_VARIABLES: 'CRM Variables',
} as const;

export type VariableShape = {
  type?: string;
  format?: string;
  grouped?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
};

// Validation codes for rendering-layer lookups only, not enforced at parse time (see
// variableValidationErrorSchema below). Distinct from CRM_V2_VALIDATION_CODES in
// crmVariableNodeV2.ts, which validates payload structure, not per-node business rules.
export const VALIDATION_CODES = [
  'NOT_DECIMAL_LITERAL',
  'NUMERIC_FORMAT_MISSING',
  'FORMAT_IS_NOT_A_TYPE',
  'ARRAY_GROUPED_MISSING',
  'GROUPED_ON_NON_ARRAY',
  'CURRENCY_METADATA_WITHOUT_FORMAT',
  'CONTAINER_VALUE_NOT_NULL',
  'NON_UNIFORM_INSTANCES',
  'UNPARSEABLE',
] as const;

export type ValidationCode = (typeof VALIDATION_CODES)[number];

export type VariableValidationError = {
  code: string;
  message: string;
};

export const variableShapeSchema = z.object({
  type: z.string().optional(),
  format: z.string().optional(),
  grouped: z.boolean().optional(),
  currencyCode: z.string().optional(),
  currencySymbol: z.string().optional(),
});

// code stays z.string(), not z.enum(VALIDATION_CODES) — an unrecognized code (e.g. MISSING_SCOPE)
// must not fail the entire array parse.
export const variableValidationErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
});

type VariableWireNodeShape = {
  name: string;
  friendlyName: string;
  path: string;
  value: string | number | null;
  type?: string;
  visible?: boolean;
  template?: string | null;
  objectId?: string;
  shape?: VariableShape;
  validationErrors?: VariableValidationError[];
  variables?: VariableWireNodeShape[] | null;
};

export const variableWireNodeSchema: z.ZodType<VariableWireNodeShape> = z.lazy(() =>
  z.object({
    name: z.string(),
    friendlyName: z.string(),
    path: z.string(),
    value: z.union([z.string(), z.number()]).nullable(),
    type: z.string().optional(),
    visible: z.boolean().optional(),
    template: z.string().nullable().optional(),
    objectId: z.string().optional(),
    shape: variableShapeSchema.optional(),
    validationErrors: z.array(variableValidationErrorSchema).optional(),
    variables: z.array(variableWireNodeSchema).nullable().optional(),
  })
);

export type VariableWireNode = z.infer<typeof variableWireNodeSchema>;

type AssertAssignable<_A extends B, B> = true;

// Per-field drift check against the generated contract. `value`, `shape`, and
// `variables` are excluded: `shape` and `variables` are intentionally
// less/more precise than the generated type, not a drift.
export type _CheckName = AssertAssignable<VariableWireNodeShape['name'], GeneratedVariableNode['name']>;
export type _CheckFriendlyName = AssertAssignable<VariableWireNodeShape['friendlyName'], GeneratedVariableNode['friendlyName']>;
export type _CheckPath = AssertAssignable<VariableWireNodeShape['path'], GeneratedVariableNode['path']>;
export type _CheckType = AssertAssignable<NonNullable<VariableWireNodeShape['type']>, GeneratedVariableNode['type']>;
export type _CheckVisible = AssertAssignable<NonNullable<VariableWireNodeShape['visible']>, GeneratedVariableNode['visible']>;
export type _CheckTemplate = AssertAssignable<NonNullable<VariableWireNodeShape['template']>, GeneratedVariableNode['template']>;
export type _CheckObjectId = AssertAssignable<NonNullable<VariableWireNodeShape['objectId']>, GeneratedVariableNode['objectId']>;

export type VariableNode = Omit<VariableWireNode, 'variables'> & {
  variables?: VariableNode[];
};
export type VariableEntity = Omit<VariableNode, 'variables' | 'path'> & { path?: string };

export type VariablesGroup = { [key in VariableEntityType]: VariableEntity[] };

export function makeFolderNode({
  name,
  friendlyName,
  path,
  variables,
}: {
  name: string;
  friendlyName: string;
  path: string;
  variables?: VariableNode[];
}): VariableNode {
  const node: VariableNode = { name, friendlyName, shape: { type: 'object' }, value: null, path };
  if (variables !== undefined) node.variables = variables;
  return node;
}

export function makeUserLeafNode({
  name,
  friendlyName,
  shape,
  value,
  path,
}: {
  name: string;
  friendlyName: string;
  shape?: VariableShape;
  value: VariableNode['value'];
  path: string;
}): VariableNode {
  const node: VariableNode = { name, friendlyName, value, path, variables: [] };
  if (shape !== undefined) node.shape = shape;
  return node;
}

export function makeRootVariableNode(): VariableNode {
  return makeFolderNode({
    name: 'root',
    friendlyName: i18next.t('editor.variables.all_variables', { defaultValue: 'All' }),
    path: '',
    variables: [],
  });
}

// Mirrors crmVariableNodeV2.ts's CONTAINER_SHAPE_TYPES — CRM v2 is the only lane emitting
// shape.type: 'array' today, but folder classification must stay in sync with that contract.
// 'object' is unconditionally a folder (ROOT_VARIABLE_NODE is object-typed with zero children
// and must still classify as a folder). 'array' is a folder only when it has children — a
// childless array shape is a grouped/repeated field represented as a single leaf entity, not
// a container to recurse into.
const UNCONDITIONAL_FOLDER_SHAPE_TYPES = ['object'];
const CHILDREN_GATED_FOLDER_SHAPE_TYPES = ['array'];

export function isVariableFolder(node: { shape?: VariableShape; variables?: unknown[] }): boolean {
  if (!node.shape) return (node.variables?.length ?? 0) > 0;
  const shapeType = node.shape.type ?? '';
  if (UNCONDITIONAL_FOLDER_SHAPE_TYPES.includes(shapeType)) return true;
  if (CHILDREN_GATED_FOLDER_SHAPE_TYPES.includes(shapeType)) return (node.variables?.length ?? 0) > 0;
  return false;
}

/**
 * Answers "are this array's children instances, or fields?" — true when a direct child is
 * itself an instance container (`.item`-shaped: `shape.type === 'object'`, or has children of
 * its own). Fallback source of truth for `shape.grouped` while v1 CRM arrays omit it on the
 * wire; an explicit `shape.grouped` always wins over this derivation.
 */
export function hasObjectInstances(node: { variables?: Array<{ shape?: VariableShape; variables?: unknown[] }> }): boolean {
  return (node.variables ?? []).some((child) => child.shape?.type === 'object' || (child.variables?.length ?? 0) > 0);
}

export function toVariableEntities(node: VariableNode): VariableEntity[] {
  const entities: VariableEntity[] = [];
  traverseTree(
    node,
    (n: VariableNode) => {
      if (isVariableFolder(n)) return;
      const { variables: _variables, ...entity } = n;
      if (entity.shape !== undefined) entity.shape = { ...entity.shape };
      entities.push(entity);
    },
    'variables'
  );
  return entities;
}
